<script setup lang="ts">
type FactIcon =
  | "trees"
  | "shield-check"
  | "key-round"
  | "sparkles"
  | "user-check"
  | "zap"
  | "wallet"
  | "languages"
  | "waves";

interface Fact {
  icon: FactIcon;
  title: string;
  note: string;
}

interface ExceptionalBlockData {
  eyebrow?: string;
  header: string;
  paragraphs: string[];
  image?: string;
  image_alt?: string;
  facts?: Fact[];
}

withDefaults(
  defineProps<{
    block: ExceptionalBlockData;
    compact?: boolean;
  }>(),
  { compact: false },
);
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-emerald-50/40 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12">
      <!-- Section header: eyebrow + italic h2 + amber divider (pattern 6.1) -->
      <header class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display italic leading-tight text-slate-900 dark:text-white"
          :class="
            compact
              ? 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl'
              : 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl'
          "
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
      </header>

      <!-- Image + paragraphs: 50/50 editorial spread.
           In compact mode the image takes 5/12 instead of 6/12 so the text
           breathes more — a small spatial cue that this is a "personal note"
           rather than a marketing block. -->
      <div class="grid gap-8 md:grid-cols-12 md:gap-12 md:items-center">
        <div :class="compact ? 'md:col-span-5' : 'md:col-span-6'">
          <NuxtImg
            v-if="block.image"
            :src="block.image"
            :alt="block.image_alt ?? block.header"
            width="1400"
            height="1050"
            format="webp"
            loading="lazy"
            sizes="100vw md:50vw"
            class="w-full object-cover"
            :class="
              compact
                ? 'h-56 rounded-xl shadow-md ring-1 ring-amber-200/40 sm:h-72 md:h-[22rem] dark:ring-amber-500/10'
                : 'h-64 rounded-2xl shadow-lg sm:h-80 md:h-[28rem] lg:h-[32rem]'
            "
          />
        </div>

        <div
          class="flex flex-col gap-4"
          :class="compact ? 'md:col-span-7' : 'md:col-span-6'"
        >
          <p
            v-for="(paragraph, i) in block.paragraphs"
            :key="i"
            class="font-body leading-relaxed text-slate-600 dark:text-slate-300"
            :class="
              compact
                ? 'text-base sm:text-lg'
                : 'text-base sm:text-lg lg:text-xl'
            "
          >
            {{ paragraph }}
          </p>
        </div>
      </div>

      <!-- Proof facts: 4-col feature strip (2x2 mobile → 4x1 desktop).
           Default = bordered "card" look (about page).
           Compact = no border, left amber accent line, icon inline-row layout
           — reads as "personal cues" instead of corporate KPIs. -->
      <div
        v-if="block.facts?.length"
        class="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mt-12 md:grid-cols-4 md:gap-4"
      >
        <article
          v-for="fact in block.facts"
          :key="fact.icon + fact.title"
          :class="
            compact
              ? 'group flex flex-col gap-2 rounded-xl border-l-2 border-l-amber-400 bg-white/70 px-4 py-4 backdrop-blur-sm transition-colors duration-300 hover:bg-amber-50/40 dark:border-l-amber-300 dark:bg-slate-800/40 dark:hover:bg-amber-500/5'
              : 'flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800/70'
          "
        >
          <div v-if="compact" class="flex items-center gap-3">
            <span
              class="flex size-8 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <Icon
                :name="`lucide:${fact.icon}`"
                class="size-4"
                aria-hidden="true"
              />
            </span>
            <h3
              class="font-display text-base italic leading-snug text-slate-900 break-words dark:text-white"
            >
              {{ fact.title }}
            </h3>
          </div>
          <template v-else>
            <span
              class="flex size-10 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300"
            >
              <Icon
                :name="`lucide:${fact.icon}`"
                class="size-5"
                aria-hidden="true"
              />
            </span>
            <h3
              class="mt-2 font-display text-base italic leading-snug text-slate-900 md:text-lg dark:text-white break-words"
            >
              {{ fact.title }}
            </h3>
          </template>
          <p
            class="font-body text-sm leading-relaxed text-slate-600 dark:text-slate-400"
            :class="compact ? 'pl-11' : ''"
          >
            {{ fact.note }}
          </p>
        </article>
      </div>
    </div>
  </section>
</template>
